import { z } from 'zod';
import {
  aggregateStyleLabels,
  calibrateStyleLabelSelection,
} from '../style-labels/aggregate-style-labels';
import {
  visualStyleLabelIdSchema,
  visualStyleVocabulary,
  type VisualStyleLabelId,
} from '../style-labels/vocabulary';
import {
  buildDeterministicStyleNarrative,
  parseStyleNarrative,
  validateStyleNarrative,
} from './narrative';

const evidenceSchema = z.string().min(3).max(180);

export const imageStyleLabelSchema = z.object({
  labelId: visualStyleLabelIdSchema,
  confidence: z.number().min(0).max(1),
  evidence: z.array(evidenceSchema).min(1).max(3),
});

export const imageStyleAnalysisSchema = z
  .object({
    imageId: z.string().min(1).max(80),
    labels: z.array(imageStyleLabelSchema).length(5),
  })
  .superRefine((analysis, context) => {
    if (new Set(analysis.labels.map((item) => item.labelId)).size !== 5) {
      context.addIssue({
        code: 'custom',
        path: ['labels'],
        message: 'Every image must have five distinct controlled labels.',
      });
    }
  });

export const candidateStyleLabelSchema = z.object({
  labelId: visualStyleLabelIdSchema,
  coverage: z.number().min(0).max(1),
  averageConfidence: z.number().min(0).max(1),
  genericness: z.number().min(0).max(1),
  distinctiveness: z.number().min(0).max(1),
  redundancyPenalty: z.number().min(0).max(1),
  score: z.number().min(0),
  sourceImageIds: z.array(z.string()).min(1).max(5),
  evidence: z.array(evidenceSchema).min(1).max(5),
});

const exactlyThreeLabelIds = z.array(visualStyleLabelIdSchema).length(3);

export const styleAnalysisDraftSchema = z.object({
  imageAnalyses: z.array(imageStyleAnalysisSchema).min(1).max(5),
  aiCoreLabelIds: exactlyThreeLabelIds,
  aiSupportingLabelIds: z.array(visualStyleLabelIdSchema).max(3),
  styleNarrative: z.string().min(1).max(1200),
  uncertainty: z.array(z.string().min(3).max(240)).max(5),
});

export const styleProfileSchema = z
  .object({
    schemaVersion: z.literal('0.2'),
    isMock: z.boolean(),
    imageAnalyses: z.array(imageStyleAnalysisSchema).min(1).max(5),
    candidateLabels: z.array(candidateStyleLabelSchema).min(3).max(30),
    aiCoreLabelIds: exactlyThreeLabelIds,
    aiSupportingLabelIds: z.array(visualStyleLabelIdSchema).max(3),
    calibratedCoreLabelIds: exactlyThreeLabelIds,
    calibratedSupportingLabelIds: z.array(visualStyleLabelIdSchema).max(3),
    styleNarrative: z.string().min(1).max(1200),
    narrativeLabelTokens: z
      .array(
        z.object({
          labelId: visualStyleLabelIdSchema,
          displayText: z.string().min(1).max(48),
        }),
      )
      .min(3)
      .max(6),
    uncertainty: z.array(z.string().min(3).max(240)).max(5),
    userAdjustments: z.object({
      edited: z.boolean(),
      previousCoreLabelIds: z.array(visualStyleLabelIdSchema).length(3).optional(),
      updatedAt: z.string().datetime().optional(),
      instruction: z.string().min(2).max(400).optional(),
      refinementProvider: z.string().min(1).max(80).optional(),
      refinementModel: z.string().min(1).max(160).optional(),
      refinementUsedFallback: z.boolean().optional(),
    }),
    provenance: z.object({
      provider: z.string().min(1).max(80),
      model: z.string().min(1).max(160),
      promptVersion: z.string().min(1).max(40),
      vocabularyVersion: z.literal(visualStyleVocabulary.version),
      generatedAt: z.string().datetime(),
      usedFallback: z.boolean(),
    }),
  })
  .superRefine((profile, context) => {
    const unique = (values: string[]) => new Set(values).size === values.length;
    if (!unique(profile.aiCoreLabelIds) || !unique(profile.calibratedCoreLabelIds)) {
      context.addIssue({
        code: 'custom',
        path: ['calibratedCoreLabelIds'],
        message: 'Core labels must be distinct.',
      });
    }
    if (
      !unique(profile.calibratedSupportingLabelIds) ||
      profile.calibratedSupportingLabelIds.some((id) => profile.calibratedCoreLabelIds.includes(id))
    ) {
      context.addIssue({
        code: 'custom',
        path: ['calibratedSupportingLabelIds'],
        message: 'Supporting labels must be distinct from core labels.',
      });
    }
    const candidateIds = new Set(profile.candidateLabels.map((item) => item.labelId));
    if (profile.calibratedCoreLabelIds.some((id) => !candidateIds.has(id))) {
      context.addIssue({
        code: 'custom',
        path: ['candidateLabels'],
        message: 'Every core label must have candidate evidence.',
      });
    }
    const narrative = validateStyleNarrative(
      profile.styleNarrative,
      profile.calibratedCoreLabelIds,
      profile.calibratedSupportingLabelIds,
    );
    if (!narrative.valid) {
      context.addIssue({
        code: 'custom',
        path: ['styleNarrative'],
        message: narrative.errors.join(' '),
      });
    }
    if (
      narrative.tokens.map((item) => `${item.labelId}|${item.displayText}`).join('::') !==
      profile.narrativeLabelTokens.map((item) => `${item.labelId}|${item.displayText}`).join('::')
    ) {
      context.addIssue({
        code: 'custom',
        path: ['narrativeLabelTokens'],
        message: 'Narrative tokens do not match markup.',
      });
    }
  });

export type StyleAnalysisDraft = z.infer<typeof styleAnalysisDraftSchema>;
export type StyleProfile = z.infer<typeof styleProfileSchema>;

export interface StyleProfileProvenance {
  provider: string;
  model: string;
  promptVersion?: string;
  generatedAt?: string;
  usedFallback?: boolean;
}

export const assembleStyleProfile = (
  draft: StyleAnalysisDraft,
  provenance: StyleProfileProvenance,
  isMock = false,
): StyleProfile => {
  const candidates = aggregateStyleLabels(draft.imageAnalyses);
  const selection = calibrateStyleLabelSelection(
    candidates,
    draft.aiCoreLabelIds,
    draft.aiSupportingLabelIds,
  );
  const suppliedNarrative = validateStyleNarrative(
    draft.styleNarrative,
    selection.core,
    selection.supporting,
  );
  const styleNarrative = suppliedNarrative.valid
    ? draft.styleNarrative
    : buildDeterministicStyleNarrative(selection.core, selection.supporting);
  const tokens = parseStyleNarrative(styleNarrative, [
    ...selection.core,
    ...selection.supporting,
  ]).tokens;
  const aiCoreLabelIds =
    new Set(draft.aiCoreLabelIds).size === 3 ? draft.aiCoreLabelIds : selection.core;
  const aiSupportingLabelIds = draft.aiSupportingLabelIds.filter(
    (id, index) => !aiCoreLabelIds.includes(id) && draft.aiSupportingLabelIds.indexOf(id) === index,
  );
  return styleProfileSchema.parse({
    schemaVersion: '0.2',
    isMock,
    imageAnalyses: draft.imageAnalyses,
    candidateLabels: candidates,
    aiCoreLabelIds,
    aiSupportingLabelIds,
    calibratedCoreLabelIds: selection.core,
    calibratedSupportingLabelIds: selection.supporting,
    styleNarrative,
    narrativeLabelTokens: tokens,
    uncertainty: [
      ...draft.uncertainty,
      ...(selection.adjusted
        ? ['The editorial label selection was calibrated against the image-level evidence.']
        : []),
      ...(!suppliedNarrative.valid
        ? ['The model narrative was replaced by a validated local rendering.']
        : []),
    ].slice(0, 5),
    userAdjustments: { edited: false },
    provenance: {
      provider: provenance.provider,
      model: provenance.model,
      promptVersion: provenance.promptVersion ?? 'visual-style-v2',
      vocabularyVersion: visualStyleVocabulary.version,
      generatedAt: provenance.generatedAt ?? new Date().toISOString(),
      usedFallback: provenance.usedFallback ?? !suppliedNarrative.valid,
    },
  });
};

const fixtureSets: VisualStyleLabelId[][] = [
  ['modernist', 'geometric', 'negative_space', 'monochrome', 'precision'],
  ['organic_modern', 'tactile', 'curved', 'earthy', 'serene'],
  ['editorial', 'high_contrast', 'sculptural', 'polished', 'cinematic'],
];

const mockDraft = (imageIds: string[]): StyleAnalysisDraft => {
  const imageAnalyses = imageIds.map((imageId, index) => ({
    imageId,
    labels: fixtureSets[index % fixtureSets.length]!.map((labelId, labelIndex) => ({
      labelId,
      confidence: 0.88 - labelIndex * 0.06,
      evidence: [
        `Fixture ${index + 1} includes deterministic visible-style evidence for this label.`,
      ],
    })),
  }));
  const preferredCore =
    imageIds.length === 1
      ? ['modernist', 'geometric', 'precision']
      : imageIds.length === 2
        ? ['modernist', 'tactile', 'geometric']
        : ['modernist', 'tactile', 'high_contrast'];
  const aiCoreLabelIds = preferredCore as [
    VisualStyleLabelId,
    VisualStyleLabelId,
    VisualStyleLabelId,
  ];
  const aiSupportingLabelIds =
    imageIds.length === 1
      ? (['negative_space', 'monochrome'] as VisualStyleLabelId[])
      : (['organic_modern', 'editorial', 'serene'] as VisualStyleLabelId[]).filter((id) =>
          imageAnalyses.some((analysis) => analysis.labels.some((label) => label.labelId === id)),
        );
  return {
    imageAnalyses,
    aiCoreLabelIds,
    aiSupportingLabelIds,
    styleNarrative: buildDeterministicStyleNarrative(aiCoreLabelIds, aiSupportingLabelIds),
    uncertainty: ['Deterministic mock analysis did not inspect image pixels.'],
  };
};

export const mockStyleProfile = (imageIds = ['image-1']): StyleProfile =>
  assembleStyleProfile(
    mockDraft(imageIds),
    {
      provider: 'mock',
      model: 'deterministic-style-fixture-v0.2',
      promptVersion: 'visual-style-mock-v2',
    },
    true,
  );

export const emptyStyleProfile = (): StyleProfile => {
  const profile = mockStyleProfile(['manual-reference']);
  return styleProfileSchema.parse({
    ...profile,
    isMock: false,
    uncertainty: ['No visual analysis was available. Choose three controlled labels to begin.'],
    provenance: {
      ...profile.provenance,
      provider: 'manual',
      model: 'none',
      usedFallback: true,
    },
  });
};

export const upgradeStyleProfileNarrative = (input: unknown): StyleProfile | null => {
  const current = styleProfileSchema.safeParse(input);
  if (current.success) return current.data;
  if (!input || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (candidate.schemaVersion !== '0.2') return null;
  const core = exactlyThreeLabelIds.safeParse(candidate.calibratedCoreLabelIds);
  const supporting = z
    .array(visualStyleLabelIdSchema)
    .max(3)
    .safeParse(candidate.calibratedSupportingLabelIds);
  if (!core.success || !supporting.success) return null;
  if (
    new Set([...core.data, ...supporting.data]).size !==
    core.data.length + supporting.data.length
  ) {
    return null;
  }
  const coreTuple = core.data as [VisualStyleLabelId, VisualStyleLabelId, VisualStyleLabelId];
  const styleNarrative = buildDeterministicStyleNarrative(coreTuple, supporting.data);
  const upgraded = styleProfileSchema.safeParse({
    ...candidate,
    styleNarrative,
    narrativeLabelTokens: parseStyleNarrative(styleNarrative, [...coreTuple, ...supporting.data])
      .tokens,
  });
  return upgraded.success ? upgraded.data : null;
};
